import { LinkButton } from "@/components/Button/Button";
import { CheckMark, Waiting } from "@/components/Icons";
import List from "@/components/List/List";
import { useApiData } from "@/contexts/ApiData";
import { useTranslations } from "@/contexts/Translation";
import { Responder } from "@/types";
import { useState } from "react";
import styles from "./Invites.module.css";

const getStatusIcon = (status: string) => {
  if (status === "responded") {
    return (
      <span className={[styles.statusIcon, styles.statusPositive].join(" ")}>
        <CheckMark />
      </span>
    );
  }
  return (
    <span className={[styles.statusIcon, styles.statusMediocre].join(" ")}>
      <Waiting />
    </span>
  );
};

const isResendAvailable = (responder: Responder, resent: string[]) => {
  if (responder.status === "responded") {
    return false;
  }
  if (responder.email.match(/anonymous\.com$/)) {
    return false;
  }
  if (resent.includes(responder.email)) {
    return false;
  }
  return true;
};

const ResponderList = ({ responders, surveyId }: { responders: Responder[]; surveyId: string }) => {
  const [resent, setResent] = useState<string[]>([]);
  const [failed, setFailed] = useState<string[]>([]);
  const t = useTranslations("invites");
  const { post } = useApiData();

  const resendInvite = async ({ email }: Responder) => {
    const response = await post(`/response/${surveyId}/responders/resend/${email}`, {});

    if (response) {
      setResent((prevResent) => [...prevResent, email]);
      setFailed((prevFailed) => prevFailed.filter((failedEmail) => failedEmail !== email));
    } else {
      setFailed((prevFailed) => (prevFailed.includes(email) ? prevFailed : [...prevFailed, email]));
    }
  };

  const isNotSent = (responder: Responder) =>
    (responder.emailFailed || failed.includes(responder.email)) && !resent.includes(responder.email);

  return (
    <List
      items={responders
        .filter((responder) => !responder.email.endsWith("anonymous.com"))
        .map((responder: Responder) => (
          <div className={styles.responderRow} key={responder.email}>
            {getStatusIcon(responder.status)}
            <div className={styles.responderMeta}>
              {responder.email} -
              {isNotSent(responder) ? (
                <span role="alert" className={[styles.responderStatus, styles.notSent].join(" ")}>
                  {t("notSent")}
                </span>
              ) : (
                <span className={[styles.responderStatus, responder.status === "responded" && styles.responded].filter(Boolean).join(" ")}>
                  {t(responder.status)}
                </span>
              )}{" "}
              - {t(responder.lang)}
              {isResendAvailable(responder, resent) && <LinkButton onClick={() => resendInvite(responder)}>{t("resendInvite")}</LinkButton>}
              {resent.includes(responder.email) && <span className={styles.resent}>{t("hasBeenResent")}</span>}
            </div>
          </div>
        ))}
    />
  );
};

export default ResponderList;
